// src/renderer/hooks/useProjectLifecycle.ts

import { useState, useCallback, useEffect } from 'react';
import { useProjectStore } from '../store/projectStore';
import { useAppStore } from '../store';
import { ProjectStateManager } from '../services/ProjectStateManager';
import { setProjectRoot, saveProjectManifest } from '../services/FileSystemService';

interface UseProjectLifecycleReturn {
  // State
  isSaving: boolean;
  isLoading: boolean;
  saveError: string | null;

  // Actions
  openProject: (handle: FileSystemDirectoryHandle, path: string) => Promise<boolean>;
  saveProject: () => Promise<boolean>;
  closeProject: () => boolean;
  saveAndClose: () => Promise<boolean>;

  // Error handling
  clearSaveError: () => void;
}

/**
 * Clear project-specific localStorage keys to prevent state bleeding between projects.
 */
const clearProjectLocalStorage = () => {
  const projectStateKeys = [
    'scene-state',
    'animation-state',
    'live-preview-state',
    'script-editor-store',
    'console-store',
    'ai-chat-store',
    'project-panel',
  ];

  projectStateKeys.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (err) {
      console.warn(`[useProjectLifecycle] Failed to clear ${key}:`, err);
    }
  });

  console.log('[useProjectLifecycle] Cleared project localStorage keys');
};

/**
 * Hook that orchestrates project open/save/close operations.
 * Manages the lifecycle of a project including state persistence,
 * dirty tracking, and keyboard shortcuts.
 */
export function useProjectLifecycle(): UseProjectLifecycleReturn {
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const {
    currentProject,
    currentProjectPath,
    setCurrentProject,
    isDirty,
    markSaved,
    setupDirtyTracking,
    cleanupDirtyTracking,
  } = useProjectStore();
  const { setCurrentView } = useAppStore();

  /**
   * Open a project from a directory handle.
   * Sets up project root, initializes state manager, loads state, and sets up dirty tracking.
   */
  const openProject = useCallback(
    async (handle: FileSystemDirectoryHandle, path: string): Promise<boolean> => {
      setIsLoading(true);
      setSaveError(null);

      // Clear any stale project state from localStorage
      clearProjectLocalStorage();

      try {
        // Set the project root for file system operations
        setProjectRoot(handle);

        // Initialize the ProjectStateManager with the project path
        ProjectStateManager.initialize(path);

        // Load all project state
        const stateBundle = await ProjectStateManager.loadProjectState();

        // Initialize all stores with the loaded state
        await ProjectStateManager.initializeStores(stateBundle);

        // Set up dirty tracking to monitor changes
        await setupDirtyTracking();

        return true;
      } catch (error) {
        console.error('Failed to open project:', error);
        setSaveError(
          error instanceof Error ? error.message : 'Failed to open project'
        );
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [setupDirtyTracking]
  );

  /**
   * Save the current project state.
   * Gathers state from all stores and persists to disk.
   */
  const saveProject = useCallback(async (): Promise<boolean> => {
    if (!currentProject || !currentProjectPath) {
      setSaveError('No project is currently open');
      return false;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      // Gather current state from all stores
      const stateBundle = await ProjectStateManager.gatherStateFromStores();

      // Save the state bundle to disk
      await ProjectStateManager.saveProjectState(stateBundle);

      // Save the updated manifest (saveProjectManifest handles modifiedAt timestamp)
      await saveProjectManifest(currentProject);

      // Update the store with the new manifest (with updated modifiedAt)
      const updatedManifest = {
        ...currentProject,
        modifiedAt: new Date().toISOString(),
      };
      setCurrentProject(updatedManifest, currentProjectPath);

      // Mark as saved (clears dirty flag)
      markSaved();

      return true;
    } catch (error) {
      console.error('Failed to save project:', error);
      setSaveError(
        error instanceof Error ? error.message : 'Failed to save project'
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [currentProject, currentProjectPath, setCurrentProject, markSaved]);

  /**
   * Close the current project.
   * Returns false if project has unsaved changes (caller should show dialog).
   * Returns true if project was closed successfully.
   */
  const closeProject = useCallback((): boolean => {
    // Check if there are unsaved changes
    if (isDirty) {
      // Return false to signal that a save dialog should be shown
      return false;
    }

    // Cleanup dirty tracking subscriptions
    cleanupDirtyTracking();

    // Reset all stores to their default state
    ProjectStateManager.resetStores();

    // Clear localStorage for project-specific state
    clearProjectLocalStorage();

    // Clear project state
    ProjectStateManager.clearProjectState();

    // Clear current project in store
    setCurrentProject(null, null);

    // Navigate to welcome screen
    setCurrentView('welcome');

    return true;
  }, [isDirty, cleanupDirtyTracking, setCurrentProject, setCurrentView]);

  /**
   * Save the project and then close it.
   * Useful when user confirms they want to save before closing.
   */
  const saveAndClose = useCallback(async (): Promise<boolean> => {
    const saved = await saveProject();
    if (!saved) {
      return false;
    }

    // Cleanup dirty tracking subscriptions
    cleanupDirtyTracking();

    // Reset all stores to their default state
    await ProjectStateManager.resetStores();

    // Clear localStorage for project-specific state
    clearProjectLocalStorage();

    // Clear project state
    ProjectStateManager.clearProjectState();

    // Clear current project in store
    setCurrentProject(null, null);

    // Navigate to welcome screen
    setCurrentView('welcome');

    return true;
  }, [saveProject, cleanupDirtyTracking, setCurrentProject, setCurrentView]);

  /**
   * Clear the save error state.
   */
  const clearSaveError = useCallback(() => {
    setSaveError(null);
  }, []);

  /**
   * Register keyboard shortcuts for project operations.
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for modifier key (Ctrl on Windows/Linux, Cmd on Mac)
      const isModifierPressed = event.ctrlKey || event.metaKey;

      if (!isModifierPressed) {
        return;
      }

      // Ctrl+S / Cmd+S - Save project and scene
      if (event.key === 's' || event.key === 'S') {
        event.preventDefault();
        // Only save if a project is open
        if (currentProject) {
          // Save current scene if one is loaded
          import('../store/sceneStore').then(async ({ useSceneStore }) => {
            const sceneStore = useSceneStore.getState();
            if (sceneStore.currentScenePath) {
              try {
                await sceneStore.saveSceneToDisk();
                console.log('[useProjectLifecycle] Scene saved');
              } catch (error) {
                console.error('[useProjectLifecycle] Failed to save scene:', error);
              }
            }
          });

          // Also save project state
          saveProject();
        }
      }

      // Ctrl+W / Cmd+W - Close project
      if (event.key === 'w' || event.key === 'W') {
        event.preventDefault();
        // Only attempt close if a project is open
        if (currentProject) {
          const closed = closeProject();
          if (!closed) {
            // Project has unsaved changes - dispatch event for dialog
            window.dispatchEvent(
              new CustomEvent('project:unsaved-close-attempt')
            );
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentProject, saveProject, closeProject]);

  return {
    isSaving,
    isLoading,
    saveError,
    openProject,
    saveProject,
    closeProject,
    saveAndClose,
    clearSaveError,
  };
}

export default useProjectLifecycle;
